import { FunctionComponent, useReducer } from "react";
import OptInSummary from "./OptInSummary";
import CheckBox from "../CheckBox/CheckBox";
import { Heading } from "../Text/Heading";
import Button from "../Button/Button";
import { UnexpectedValueError } from "../../../lib/unexpected-value-error";
import { AnswerChoice, FormData, toFormData, UploadedFile } from "../form-data";
import { ComponentOptInLink, ComponentOptInSummary, PageOptIn } from "../../../lib/ssi/form.types";
import { ComponentId, PageId } from "../../../lib/types/ids";
import { Score } from "../../../lib/types/form/submission/score";
import { UnencryptedAnswers } from "../../../lib/types/form/submission/answer";
import SignField from "../Sign/SignField";
import { ValidateResultInline } from "../../../lib/validations/validate";

type OptInProps = {
  data: PageOptIn;
  onDataSelected: (item: FormData) => void;
  onBack?: () => void;
  resolveExpression: (expression: string) => unknown;
  errors: { [key: string]: ValidateResultInline };
  answers: UnencryptedAnswers;
  scoreExpression: string | undefined;
  score: { [key: PageId]: Score };
  isLoading: boolean;
  isActive: boolean;
};

type InputValue = string | boolean | UploadedFile;
type FormInput = Record<ComponentId, InputValue>;

type InputValueChange = InputValue | undefined;
type FormInputChange = Record<ComponentId, InputValueChange>;

const OptIn: FunctionComponent<OptInProps> = ({
  data,
  onDataSelected,
  resolveExpression,
  answers,
  errors,
  scoreExpression,
  score,
  isLoading,
  isActive,
}) => {
  const initialState: FormInput = {};
  data.components.forEach((el) => {
    if (el.componentType === "input") {
      const id = el.id;
      initialState[id] = "";
    }
  });
  const [formInput, setFormInput] = useReducer(
    (state: FormInput, newState: FormInputChange) => {
      const updatedState = { ...state };
      for (const key of Object.keys(newState)) {
        const item = newState[key];
        if (item === undefined) {
          delete updatedState[key];
        } else {
          updatedState[key] = item;
        }
      }
      return updatedState;
    },
    initialState
  );
  const onInputChange = (value: string | boolean | UploadedFile | undefined, id: string) => {
    if (value === true) {
      setFormInput({ [id]: "accepted" });
    } else if (value === false) {
      setFormInput({ [id]: false });
    } else {
      setFormInput({ [id]: value });
    }
  };
  const checkboxes = data.components.map((el) => {
    if (el.componentType === "input" && el.type === "checkbox") {
      if (el.condition && !resolveExpression(el.condition))
        return;

      return (
        <div className="mb-8 component" key={el.id}
          data-id={ComponentId.encode(el.id)}>
          <CheckBox
            {...el}
            checked={!!formInput[el.id]}
            setChecked={onInputChange}
            error={!!errors[el.id]}
            errorText={errors[el.id]?.text}
          />
        </div>
      );
    }
    return null;
  });
  const signatures = data.components.map((el) => {
    if (el.componentType === "input" && el.type === "signature") {
      if (el.condition && !resolveExpression(el.condition))
        return;

      return (
        <div className="mb-8 component" key={el.id}
          data-id={ComponentId.encode(el.id)}>
          <SignField {...el}
            setFile={onInputChange}
            error={!!errors[el.id]}
            errorText={errors[el.id]?.text}
            isActive={isActive} />
        </div>
      );
    }
    return null;
  });
  const summary = (function (): ComponentOptInSummary | undefined {
    for (const el of data.components) {
      if (el.componentType === "summary") {
        return el;
      }
    }
    return undefined;
  })();
  if (!summary) {
    throw new UnexpectedValueError(summary);
  }
  const linksArray = summary?.links || [];
  const links = linksArray.map((el: ComponentOptInLink, index: number) => {
    return (
      // <a target="_blank" href={el.link}>
      <Button
        key={index}
        elementType="a"
        target="_blank"
        href={el.link}
        variant="outline"
        size="small"
        className={`w-1/2 text-center ${index > 0 ? "ml-2" : ""}`}
      >
        {el.label}
      </Button>
      // </a>
    );
  });
  const onSelectData = (customAnswer?: AnswerChoice) => {
    onDataSelected(
      toFormData("opt-in", data, formInput, customAnswer)
    );
  };

  const cta = summary.cta;
  const confirmGroup = [
    <p className="font-medium mb-4">
      Schließe deinen Mitgliedschaftsvertrag mit einem Klick auf den
      untenstehenden Button verbindlich ab.
    </p>,
    <Button
      variant="primary"
      className="w-full mb-2"
      isLoading={isLoading}
      onPress={() => onSelectData()}
    >
      {cta.length == 0 ? "Kostenpflichtige Mitgliedschaft abschließen" : cta}
    </Button>,
    <p className="text-center text-sm text-gray-500">
      Deine Daten werden ausschließlich verschlüsselt übermittelt.
    </p>
  ];

  return (
    <div className="w-full max-w-4xl">
      <div className="flex flex-wrap items-stretch mb-12 lg:-mx-8">
        <div className="px-2 py-2 w-full lg:py-0 lg:px-8 lg:w-1/2">
          <OptInSummary {...summary} questionId={data.id} answers={answers}
            resolveExpression={resolveExpression}
            scoreExpression={scoreExpression}
            score={score} />
        </div>
        <div className="px-2 py-2 w-full lg:py-0 lg:px-8 lg:w-1/2">
          <Heading level={4} className="mb-4">
            Letzter Schritt
          </Heading>
          <div className="flex w-full -px-4 mb-8">{links}</div>
          <div className="mb-16">{checkboxes}</div>
          {signatures.length ? null : confirmGroup}
        </div>
      </div>
      {signatures}
      {!signatures.length ? null : confirmGroup}
    </div>
  );
};

export default OptIn;
